import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Bell } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type MouseEvent } from 'react';
import { acikEkraniTazele } from '@/components/layout/navigation';
import { Button } from '@/components/ui/button';
import { onayla } from '@/components/ui/onay-penceresi';
import { useAuth } from '@/features/auth/auth-context';
import { ApiError, api } from '@/lib/api';

/**
 * §18.1 — Uygulama içi bildirim zili.
 *
 * ⚠️ Bu bileşen 6 Eylül 2026'da eklendi. O güne kadar `GET /notifications`
 * ucu VARDI (kodda "Zil ikonu — okunmamış bildirimler" yorumuyla) ama
 * arayüzde onu çağıran hiçbir şey YOKTU.
 *
 * Ölçüldü: üretimde **746 bildirimin 746'sı okunmamış** — çünkü okunacak
 * bir yer yoktu. Aralarında 45 yeni sipariş, 206 marj ihlali ve kapatılamaz
 * olarak tanımlanmış kritik uyarılar vardı. Kullanıcı hiçbirini görmedi.
 *
 * Bu, deponun kendi "yolun yarısı" hata sınıfı: üretici çalışıyor, tüketici
 * hiç yazılmamış.
 */

interface Bildirim {
  id: string;
  /** Kişisel güvenlik bildirimiyse sahibi; genel bildirimde `null`. */
  userId: string | null;
  type: string;
  severity: 'INFO' | 'WARNING' | 'ERROR' | 'CRITICAL';
  title: string;
  body: string | null;
  link: string | null;
  repeatCount: number;
  lastRepeatAt: string | null;
  createdAt: string;
}

interface BildirimListesi {
  items: Bildirim[];
  /** Tüm okunmamışlar: `items` yalnız istenen kadarı. */
  total: number;
}

const SAYFA = 50;
/** API'nin `ZIL_SATIR_SINIRI` değeri. */
const UST_SINIR = 1_000;

/**
 * Jeton sınıfları: Tailwind'in `dark:`i panelin temasına değil işletim sistemine bakıyor. Mac koyu,
 * panel açık temadayken uyarı başlıkları beyaz üstüne soluk sarı (1,67:1) çıkıyordu.
 */
const RENK: Record<Bildirim['severity'], string> = {
  INFO: 'font-medium text-fg',
  WARNING: 'font-medium text-warning',
  ERROR: 'font-medium text-danger',
  CRITICAL: 'font-semibold text-danger',
};

/** Satırdaki olayın son görüldüğü an: tekrarlayan uyarı "23 saat önce" görünmesin. */
function sonGorulme(b: Bildirim): string {
  return b.lastRepeatAt ?? b.createdAt;
}

/**
 * Bildirim gövdelerindeki `**kalın**` işaretlerini gerçekten kalın yapar.
 *
 * ⚠️ Gövdeler sunucuda Markdown vurgusuyla yazılıyor (ör. MARGIN_VIOLATION:
 * "**296 tanesi maliyetin de altında**"). Düz metin olarak basılınca
 * kullanıcı yıldızları OLDUĞU GİBİ görüyordu. Tam bir Markdown ayrıştırıcı
 * gerekmiyor; tek kalıp bu ve bildirim gövdeleri güvenilir kaynaktan (kendi
 * sunucumuz) geliyor — yine de HTML enjekte edilmiyor, React düğümü
 * üretiliyor.
 */
function kalinla(metin: string): React.ReactNode[] {
  return metin.split(/(\*\*[^*]+\*\*)/g).map((parca, i) =>
    parca.startsWith('**') && parca.endsWith('**') && parca.length > 4 ? (
      <strong key={i} className="font-semibold text-fg">
        {parca.slice(2, -2)}
      </strong>
    ) : (
      parca
    ),
  );
}

function neZamanOnce(iso: string): string {
  const dk = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000);
  if (dk < 1) return 'az önce';
  if (dk < 60) return `${dk} dk önce`;
  const saat = Math.floor(dk / 60);
  if (saat < 24) return `${saat} saat önce`;
  return `${Math.floor(saat / 24)} gün önce`;
}

export function BildirimZili() {
  const [acik, setAcik] = useState(false);
  const kutuRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const router = useRouter();
  const { user } = useAuth();
  // Genel satırda okundu herkes için geçerli: izleyici yalnız kendi bildirimini kapatabiliyor
  // (notification.controller.ts)
  const yetkili = user?.role === 'OWNER' || user?.role === 'ADMIN';
  const okunabilir = (b: Bildirim) => yetkili || (b.userId != null && b.userId === user?.id);
  const [limit, setLimit] = useState(SAYFA);

  const bildirimler = useQuery({
    queryKey: ['notifications', 'unread', limit],
    queryFn: () =>
      api.get<BildirimListesi>(limit === SAYFA ? '/notifications' : `/notifications?limit=${limit}`),
    placeholderData: (onceki) => onceki,
    /*
     * Dakikada bir tazeleniyor: yeni sipariş ve kritik uyarılar için yeterli.
     *
     * ⚠️ `staleTime` ŞART. Zil her sayfada duruyor; onsuz TanStack Query her
     * gezinmede yeniden istek atıyor ve bu, uygulamanın istek hacmini sayfa
     * başına bir istek artırıyor. E2E ekran turunda ölçüldü: 81 ekran hızla
     * gezilince dakikada 300 istek sınırı aşıldı, başka uçlar 429 aldı ve
     * beş ekran hata ekranına düştü. Gerçek kullanımda da gereksiz yük.
     *
     * `staleTime` aralığın hemen altında: bir dakika içindeki gezinmeler
     * önbellekten okuyor, tazeleme yine dakikada bir oluyor.
     */
    staleTime: 55_000,
    refetchInterval: 60_000,
    // Sekmeye her dönüşte yeniden sormaya da gerek yok; aralık zaten var.
    refetchOnWindowFocus: false,
  });

  const okunduIsaretle = useMutation({
    mutationFn: (ids?: string[]) => api.post<{ read: number }>('/notifications/read', ids ? { ids } : {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'unread'] });
    },
  });

  useEffect(() => {
    if (!acik) {
      // Büyütülmüş liste panel kapalıyken dakikada bir yeniden çekilmesin
      setLimit(SAYFA);
      return;
    }
    const onClick = (e: globalThis.MouseEvent) => {
      if (kutuRef.current && !kutuRef.current.contains(e.target as Node)) setAcik(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAcik(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [acik]);

  const liste = useMemo(
    () =>
      [...(bildirimler.data?.items ?? [])].sort((a, b) =>
        sonGorulme(b).localeCompare(sonGorulme(a)),
      ),
    [bildirimler.data],
  );
  const sayi = bildirimler.data?.total ?? 0;

  const tumunuOkunduSay = async () => {
    const evet = await onayla({
      baslik: 'Bildirimler okundu sayılsın mı?',
      metin:
        `${sayi.toLocaleString('tr-TR')} okunmamış bildirimin tamamı okundu sayılacak; listede ` +
        'henüz görünmeyen eskiler de dahil. Okunan bildirimler bir daha listelenmez, bu işlem ' +
        'geri alınamaz.',
      onayEtiketi: 'Tümünü okundu say',
    });
    if (evet) okunduIsaretle.mutate(undefined);
  };

  const ac = (e: MouseEvent<HTMLAnchorElement>, b: Bildirim) => {
    const adres = b.link as string;
    // Yeni sekmede açma isteği tarayıcıda kalsın
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      if (okunabilir(b)) okunduIsaretle.mutate([b.id]);
      return;
    }
    e.preventDefault();
    void (async () => {
      if (okunabilir(b)) await okunduIsaretle.mutateAsync([b.id]).catch(() => undefined);
      setAcik(false);
      // Hedef açık ekransa adresi yeniden okuması gerekiyor (`?tab=`, `?durum=`, `?sekme=`)
      if (adres.split('?')[0] === window.location.pathname) acikEkraniTazele(adres);
      else router.history.push(adres);
    })();
  };

  return (
    <div className="relative" ref={kutuRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setAcik((v) => !v)}
        aria-expanded={acik}
        aria-haspopup="dialog"
        aria-label={
          sayi > 0 ? `Bildirimler (${sayi.toLocaleString('tr-TR')} okunmamış)` : 'Bildirimler'
        }
        title="Bildirimler"
      >
        <Bell aria-hidden="true" />
        {sayi > 0 && (
          <span
            className="absolute right-0.5 top-0.5 flex min-w-[1.1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[0.625rem] font-semibold leading-4 text-white"
            aria-hidden="true"
          >
            {sayi > 99 ? '99+' : sayi}
          </span>
        )}
      </Button>

      {acik && (
        <div
          role="dialog"
          aria-label="Bildirimler"
          /*
           * ⚠️ `bg-bg-elevated` — `bg-surface` DEĞİL.
           *
           * `--surface` diye bir anahtar yok (globals.css'te tanımlı olanlar:
           * bg, bg-elevated, bg-sunken, fg, fg-muted, fg-subtle, border,
           * border-strong, accent…). Tanımsız bir sınıf Tailwind'de hiçbir
           * kural üretmiyor: hata da vermiyor, panel sessizce SAYDAM kalıyor
           * ve arkadaki tablo metnin içinden görünüyor — kullanıcı 6 Eylül
           * 2026'da ekran görüntüsüyle bildirdi. Aynı dosyadaki hesap menüsü
           * baştan beri `bg-bg-elevated` kullanıyordu.
           */
          /*
           * Zilin sağında üç düğme var: panel zile yaslanınca 548 px'ten dar ekranda sol kenarı
           * ekranın dışına çıkıyordu (390 px'te -100 px). Telefonda ekrana göre konumlanıyor.
           */
          className="absolute right-0 z-50 mt-2 max-h-[70vh] w-[min(26rem,calc(100vw-2rem))] overflow-y-auto rounded-[var(--radius)] border border-border bg-bg-elevated shadow-[var(--shadow-md)] max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:top-[calc(var(--nav-h)+0.5rem)] max-sm:mt-0 max-sm:w-auto"
        >
          <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
            <span className="text-sm font-medium">
              Bildirimler{' '}
              {sayi > 0 && (
                <span className="text-fg-subtle">({sayi.toLocaleString('tr-TR')})</span>
              )}
            </span>
            {sayi > 0 && yetkili && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void tumunuOkunduSay()}
                disabled={okunduIsaretle.isPending}
              >
                Tümünü okundu say
              </Button>
            )}
          </div>

          {bildirimler.isLoading && <p className="p-4 text-sm text-fg-muted">Yükleniyor…</p>}

          {/* Hata "bildirim yok" değildir: kritik uyarılar varken güven vermesin */}
          {bildirimler.isError && (
            <div role="alert" className="flex items-center justify-between gap-2 p-3 text-sm">
              <span className="text-danger">Bildirimler yüklenemedi.</span>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => void bildirimler.refetch()}
                disabled={bildirimler.isFetching}
              >
                Yeniden dene
              </Button>
            </div>
          )}

          {okunduIsaretle.isError && (
            <p role="alert" className="px-3 pt-2 text-xs text-danger">
              {okunduIsaretle.error instanceof ApiError
                ? okunduIsaretle.error.message
                : 'Bildirim okundu sayılamadı. Tekrar deneyin.'}
            </p>
          )}

          {!bildirimler.isLoading && !bildirimler.isError && sayi === 0 && (
            <p className="p-4 text-sm text-fg-muted">Okunmamış bildiriminiz yok.</p>
          )}

          <ul className="divide-y divide-border">
            {liste.map((b) => (
              <li key={b.id} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-2">
                  {/* Başlık ve gövde ciro, kâr ve sipariş taşıyor: sunum modunda bulanık */}
                  <span className={`hassas text-sm ${RENK[b.severity]}`}>{b.title}</span>
                  <span className="shrink-0 text-[0.6875rem] text-fg-subtle">
                    {neZamanOnce(sonGorulme(b))}
                  </span>
                </div>

                {b.body !== null && b.body !== '' && (
                  /* `whitespace-pre-line`: gövdeler satır sonlarıyla yazılıyor. */
                  <p className="hassas mt-1 whitespace-pre-line text-xs text-fg-muted">
                    {kalinla(b.body)}
                  </p>
                )}

                <div className="mt-1.5 flex items-center gap-3">
                  {b.link !== null && b.link !== '' && (
                    <a
                      href={b.link}
                      onClick={(e) => ac(e, b)}
                      className="text-xs text-accent hover:underline"
                    >
                      Aç
                    </a>
                  )}
                  {b.repeatCount > 1 && (
                    <span className="text-[0.6875rem] text-fg-subtle">
                      {b.repeatCount} kez tekrarlandı
                    </span>
                  )}
                  {okunabilir(b) && (
                    <button
                      type="button"
                      className="ml-auto text-[0.6875rem] text-fg-muted hover:underline"
                      onClick={() => okunduIsaretle.mutate([b.id])}
                    >
                      Okundu
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>

          {liste.length < sayi &&
            (limit < UST_SINIR ? (
              <div className="border-t border-border p-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full"
                  onClick={() => setLimit((l) => Math.min(l + SAYFA, UST_SINIR))}
                  disabled={bildirimler.isFetching}
                >
                  Daha eski bildirimleri göster ({(sayi - liste.length).toLocaleString('tr-TR')})
                </Button>
              </div>
            ) : (
              <p className="border-t border-border p-3 text-xs text-fg-muted">
                En yeni {UST_SINIR.toLocaleString('tr-TR')} bildirim gösteriliyor. Okudukça eskileri
                listeye girer.
              </p>
            ))}
        </div>
      )}
    </div>
  );
}
